import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, TextField, Grid, Card, CircularProgress, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, 
    InputLabel, Select, MenuItem, Checkbox, ListItemText, OutlinedInput
} from '@mui/material';
import { ChevronLeftRounded, ChevronRightRounded, SaveRounded, FlashOnRounded, CloseRounded } from '@mui/icons-material';
import { doc, getDoc, collection, query, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase';

// Premium Theme Palette
const THEME = {
    BG_DARK: "#06362D",
    PANEL_DARK: "#06362D",
    DEEP_DARK: "#04241E",
    BORDER_DARK: "#D1A505",
    TEXT_PRIMARY: "#DEE2B1",
    TEXT_SECONDARY: "#D1A505",
    ACCENT_GOLD: "#FFD500",
    STRICT_DARK_BORDER: "rgba(0, 0, 0, 0.5)",
    DANGER: "#EF4444",
    DANGER_BG: "rgba(239, 68, 68, 0.15)",
    SUCCESS: "#10B981",
    SUCCESS_BG: "rgba(16, 185, 129, 0.15)",
    WARNING: "#F59E0B",
    WARNING_BG: "rgba(245, 158, 11, 0.15)"
};

// Helper to format local date to YYYY-MM-DD
const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

function RateUpdates() {
    const [loading, setLoading] = useState(true);
    const [propertyId, setPropertyId] = useState("");
    const [authConfig, setAuthConfig] = useState({ hotelCode: "", authCode: "" });
    const [otaMeta, setOtaMeta] = useState({ otas: [], roomMappings: [] });
    const [savedUpdates, setSavedUpdates] = useState([]);
    
    // Calendar View State
    const [viewStartDate, setViewStartDate] = useState(() => formatDate(new Date()));

    // Grid Edit State (Stores all modified rates)
    const [pendingEdits, setPendingEdits] = useState({});
    const [isSavingAll, setIsSavingAll] = useState(false);

    // Bulk Update Modal States
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkRoomId, setBulkRoomId] = useState("");
    const [bulkPlanId, setBulkPlanId] = useState("");
    const [bulkOtas, setBulkOtas] = useState([]);
    const [bulkStartDate, setBulkStartDate] = useState("");
    const [bulkEndDate, setBulkEndDate] = useState("");
    const [bulkPrice, setBulkPrice] = useState("");
    const [isBulkSyncing, setIsBulkSyncing] = useState(false);

    useEffect(() => {
        const fetchUser = async () => {
            const user = auth.currentUser;
            if (user) {
                const snap = await getDoc(doc(db, "authroles", user.uid));
                if (snap.exists()) setPropertyId(snap.data().propertyId);
            }
        };
        fetchUser();
    }, []);

    useEffect(() => {
        if (!propertyId) return;

        const q = query(collection(db, "ota_rate_updates"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const updates = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(item => item.propertyId === propertyId);
            setSavedUpdates(updates);
        });

        const fetchInventoryAndOTAs = async () => {
            try {
                const configSnap = await getDoc(doc(db, "channel_manager", propertyId));
                let hotelConfig = { hotelCode: "", authCode: "" };
                
                if (configSnap.exists()) {
                    hotelConfig = configSnap.data();
                    setAuthConfig({ hotelCode: hotelConfig.hotelCode, authCode: hotelConfig.authCode });
                    
                    const res = await fetch('https://navajowhite-wren-495848.hostingersite.com/api/ota-info', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ HotelCode: hotelConfig.hotelCode, AuthCode: hotelConfig.authCode })
                    });
                    const data = await res.json();
                    
                    const invSnap = await getDoc(doc(db, "ota_room_inventory", propertyId));
                    const rooms = invSnap.exists() ? invSnap.data().rooms : [];

                    if (data.APIResponse?.IsSuccess) {
                        setOtaMeta({ otas: data.OTAs || [], roomMappings: rooms });
                    }
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchInventoryAndOTAs();
        return () => unsubscribe();
    }, [propertyId]);

    const calendarDates = Array.from({ length: 7 }).map((_, i) => {
        const d = new Date(viewStartDate);
        d.setDate(d.getDate() + i);
        return formatDate(d);
    });

    const shiftDate = (days) => {
        const d = new Date(viewStartDate);
        d.setDate(d.getDate() + days);
        setViewStartDate(formatDate(d));
    };

    const getRateForDate = (roomId, planId, ota, targetDate) => {
        const applicable = savedUpdates.filter(u =>
            String(u.roomId) === String(roomId) &&
            String(u.ratePlanId) === String(planId) &&
            String(u.otaCode) === String(ota) &&
            u.startDate <= targetDate &&
            u.endDate >= targetDate
        );

        if (applicable.length === 0) return "-";
        
        applicable.sort((a, b) => {
            const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
            const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
            return timeB - timeA;
        });

        return applicable[0].price.toString();
    };

    const handleCellChange = (cellKey, originalRate, newValue) => {
        if (newValue === originalRate) {
            const newEdits = { ...pendingEdits };
            delete newEdits[cellKey];
            setPendingEdits(newEdits);
        } else {
            setPendingEdits(prev => ({ ...prev, [cellKey]: newValue }));
        }
    };

    const handleSaveAll = async () => {
        const validEdits = Object.entries(pendingEdits).filter(([_, val]) => val.trim() !== "");
        if (validEdits.length === 0) return;

        setIsSavingAll(true);
        try {
            const ratePayload = [];
            const firebaseDocsToSave = [];

            for (const [key, newRate] of validEdits) {
                const [roomId, planId, otaCode, ...dateParts] = key.split('-');
                const targetDate = dateParts.join('-');

                const room = otaMeta.roomMappings.find(r => String(r.RoomId) === roomId);
                const plan = room?.RoomRatePlans?.find(p => String(p.RatePlanId) === planId);
                const ota = otaMeta.otas.find(o => o.OTACode === otaCode);

                ratePayload.push({
                    FromDate: targetDate,
                    ToDate: targetDate,
                    RoomId: roomId,
                    RatePlanId: planId,
                    Rate: newRate,
                    ExtraAdultPriceSpecified: "true",
                    ExtraChildPriceSpecified: "false",
                    OTASpecified: "true",
                    OTACode: otaCode
                });

                firebaseDocsToSave.push({
                    propertyId,
                    roomId: roomId,
                    roomName: room?.RoomName || "Unknown",
                    ratePlanId: planId,
                    ratePlanName: plan?.RatePlanName || "EP",
                    price: newRate, 
                    startDate: targetDate, 
                    endDate: targetDate, 
                    otaCode: otaCode,
                    otaName: ota?.OTAName || otaCode,
                    status: "Synced",
                    createdAt: serverTimestamp(),
                    syncedAt: new Date().toISOString()
                });
            }

            const response = await fetch('https://navajowhite-wren-495848.hostingersite.com/api/rate-update', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Authentication: { HotelCode: authConfig.hotelCode, AuthCode: authConfig.authCode },
                    Rates: { Rate: ratePayload }
                })
            });

            const result = await response.json();
            
            if (result.APIResponse?.IsSuccess) {
                await Promise.all(firebaseDocsToSave.map(docData => 
                    addDoc(collection(db, "ota_rate_updates"), docData)
                ));
                
                setPendingEdits({});
                alert("All rates successfully updated and synced!");
            } else {
                alert("OTA API Update Failed: " + (result.APIResponse?.Message || "Unknown error"));
            }
        } catch (err) {
            console.error("Save Error:", err);
            alert("Failed to save changes. Please try again.");
        } finally {
            setIsSavingAll(false);
        }
    };

    const resetBulkForm = () => {
        setBulkRoomId("");
        setBulkPlanId("");
        setBulkOtas([]);
        setBulkStartDate("");
        setBulkEndDate("");
        setBulkPrice("");
    };

    const handleBulkSave = async () => {
        if (!bulkRoomId || !bulkPlanId || !bulkPrice || !bulkStartDate || !bulkEndDate || bulkOtas.length === 0) {
            return alert("Please fill all fields.");
        }

        setIsBulkSyncing(true);
        const room = otaMeta.roomMappings.find(r => String(r.RoomId) === String(bulkRoomId));
        const plan = room?.RoomRatePlans?.find(p => String(p.RatePlanId) === String(bulkPlanId));

        try {
            for (const otaCode of bulkOtas) {
                const ratePayload = [{
                    FromDate: bulkStartDate,
                    ToDate: bulkEndDate,
                    RoomId: bulkRoomId.toString(),
                    RatePlanId: bulkPlanId.toString(),
                    Rate: bulkPrice.toString(),
                    ExtraAdultPriceSpecified: "true",
                    ExtraChildPriceSpecified: "false",
                    OTASpecified: "true",
                    OTACode: otaCode
                }];

                const response = await fetch('https://navajowhite-wren-495848.hostingersite.com/api/rate-update', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        Authentication: { HotelCode: authConfig.hotelCode, AuthCode: authConfig.authCode },
                        Rates: { Rate: ratePayload }
                    })
                });

                const result = await response.json();
                
                if (result.APIResponse?.IsSuccess) {
                    await addDoc(collection(db, "ota_rate_updates"), {
                        propertyId,
                        roomId: bulkRoomId.toString(),
                        roomName: room?.RoomName || "Unknown",
                        ratePlanId: bulkPlanId.toString(),
                        ratePlanName: plan?.RatePlanName || "EP",
                        price: bulkPrice, 
                        startDate: bulkStartDate, 
                        endDate: bulkEndDate, 
                        otaCode: otaCode,
                        otaName: otaMeta.otas.find(o => o.OTACode === otaCode)?.OTAName || otaCode,
                        status: "Synced",
                        createdAt: serverTimestamp(),
                        syncedAt: new Date().toISOString()
                    });
                }
            }
            alert("Bulk rates successfully synced to OTAs!");
            setBulkOpen(false);
            resetBulkForm();
        } catch (err) { 
            alert("Bulk update failed. Please try again."); 
        } finally {
            setIsBulkSyncing(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: THEME.BG_DARK }}>
                <CircularProgress size={36} sx={{ color: THEME.ACCENT_GOLD }} />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', bgcolor: THEME.BG_DARK, minHeight: '100vh', color: THEME.TEXT_PRIMARY }}>
            <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
                
                {/* Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: THEME.ACCENT_GOLD, letterSpacing: '-0.02em' }}>
                            Direct Rates Calendar
                        </Typography>
                        <Typography variant="caption" sx={{ color: THEME.TEXT_PRIMARY, opacity: 0.85 }}>
                            Update rates directly in the grid and click save all, or use bulk update.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2} alignItems="center">
                        {Object.keys(pendingEdits).length > 0 && (
                            <Button 
                                variant="contained" 
                                disableElevation
                                onClick={handleSaveAll}
                                disabled={isSavingAll}
                                startIcon={isSavingAll ? <CircularProgress size={16} sx={{ color: THEME.DEEP_DARK }} /> : <SaveRounded />}
                                sx={{ 
                                    borderRadius: 2, 
                                    textTransform: 'none', 
                                    fontWeight: 800,
                                    bgcolor: THEME.ACCENT_GOLD,
                                    color: THEME.DEEP_DARK,
                                    boxShadow: '0 4px 14px 0 rgba(255, 213, 0, 0.35)',
                                    '&:hover': { bgcolor: THEME.BORDER_DARK, color: '#FFFFFF' }
                                }}
                            >
                                {isSavingAll ? "Saving..." : `Save All Changes (${Object.keys(pendingEdits).length})`}
                            </Button>
                        )}
                        <Button 
                            variant="outlined" 
                            disableElevation
                            startIcon={<FlashOnRounded sx={{ color: THEME.ACCENT_GOLD }} />}
                            onClick={() => setBulkOpen(true)}
                            sx={{ 
                                borderRadius: 2, 
                                textTransform: 'none', 
                                fontWeight: 700, 
                                borderColor: THEME.BORDER_DARK, 
                                color: THEME.TEXT_PRIMARY, 
                                bgcolor: THEME.DEEP_DARK,
                                '&:hover': { borderColor: THEME.ACCENT_GOLD, bgcolor: THEME.PANEL_DARK, color: THEME.ACCENT_GOLD }
                            }}
                        >
                            Bulk Update
                        </Button>
                    </Stack>
                </Stack>

                {/* Calendar Card */}
                <Grid className="row" spacing={2}>
                    <Grid className="col-sm-12" item xs={12}>
                        <Card sx={{ 
                            borderRadius: 3, 
                            border: `1px solid ${THEME.BORDER_DARK}`, 
                            bgcolor: THEME.PANEL_DARK,
                            boxShadow: `0px 4px 24px ${THEME.STRICT_DARK_BORDER}`,
                            overflow: 'hidden' 
                        }}>
                            {/* Controls Bar */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: THEME.DEEP_DARK, borderBottom: `1px solid ${THEME.BORDER_DARK}` }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: THEME.TEXT_PRIMARY }}>
                                    7-Day Rate Outlook
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <IconButton 
                                        size="small" 
                                        onClick={() => shiftDate(-7)} 
                                        sx={{ color: THEME.ACCENT_GOLD, bgcolor: THEME.PANEL_DARK, border: `1px solid ${THEME.BORDER_DARK}`, '&:hover': { bgcolor: THEME.BG_DARK } }}
                                    >
                                        <ChevronLeftRounded />
                                    </IconButton>
                                    <Typography variant="body2" sx={{ fontWeight: 800, px: 2, color: THEME.TEXT_PRIMARY }}>
                                        {new Date(viewStartDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                    </Typography>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => shiftDate(7)} 
                                        sx={{ color: THEME.ACCENT_GOLD, bgcolor: THEME.PANEL_DARK, border: `1px solid ${THEME.BORDER_DARK}`, '&:hover': { bgcolor: THEME.BG_DARK } }}
                                    >
                                        <ChevronRightRounded />
                                    </IconButton>
                                </Stack>
                            </Box>
                            
                            <TableContainer sx={{ maxHeight: 'calc(100vh - 220px)' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem', width: '22%', bgcolor: THEME.DEEP_DARK, color: THEME.ACCENT_GOLD, borderRight: `1px solid ${THEME.BORDER_DARK}`, borderBottom: `1px solid ${THEME.BORDER_DARK}`, zIndex: 3 }}>
                                                ROOM / PLAN / OTA
                                            </TableCell>
                                            {calendarDates.map(date => {
                                                const d = new Date(date);
                                                return (
                                                    <TableCell key={date} align="center" sx={{ fontWeight: 800, fontSize: '0.75rem', bgcolor: THEME.DEEP_DARK, color: THEME.TEXT_PRIMARY, borderRight: `1px solid ${THEME.BORDER_DARK}`, borderBottom: `1px solid ${THEME.BORDER_DARK}`, minWidth: 100, zIndex: 2 }}>
                                                        <span style={{ color: THEME.TEXT_SECONDARY, textTransform: 'uppercase' }}>
                                                            {d.toLocaleDateString('en-US', { weekday: 'short' })}
                                                        </span>
                                                        <br/>
                                                        <span>{d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody sx={{ bgcolor: THEME.PANEL_DARK }}>
                                        {otaMeta.roomMappings.map((room) => (
                                            room.RoomRatePlans?.map((plan) => (
                                                otaMeta.otas.map((ota) => (
                                                    <TableRow key={`${room.RoomId}-${plan.RatePlanId}-${ota.OTACode}`} sx={{ '&:hover': { bgcolor: `${THEME.DEEP_DARK}88` } }}>
                                                        <TableCell sx={{ borderRight: `1px solid ${THEME.BORDER_DARK}`, borderBottom: `1px solid ${THEME.BORDER_DARK}` }}>
                                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, color: THEME.TEXT_PRIMARY }}>{room.RoomName}</Typography>
                                                            <Typography variant="caption" sx={{ display: 'block', color: THEME.TEXT_PRIMARY, opacity: 0.7, fontSize: '0.65rem' }}>{plan.RatePlanName}</Typography>
                                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: THEME.ACCENT_GOLD, fontSize: '0.65rem' }}>{ota.OTAName}</Typography>
                                                        </TableCell>
                                                        
                                                        {calendarDates.map(date => {
                                                            const originalRate = getRateForDate(room.RoomId, plan.RatePlanId, ota.OTACode, date);
                                                            const cellKey = `${room.RoomId}-${plan.RatePlanId}-${ota.OTACode}-${date}`;
                                                            
                                                            const displayRate = pendingEdits[cellKey] !== undefined ? pendingEdits[cellKey] : (originalRate !== "-" ? originalRate : "");
                                                            const isEdited = pendingEdits[cellKey] !== undefined;
                                                            
                                                            return (
                                                                <TableCell key={date} align="center" sx={{ borderRight: `1px solid ${THEME.BORDER_DARK}`, borderBottom: `1px solid ${THEME.BORDER_DARK}`, p: 1, bgcolor: isEdited ? THEME.WARNING_BG : 'transparent' }}>
                                                                    <TextField 
                                                                        size="small"
                                                                        variant="outlined"
                                                                        placeholder="-"
                                                                        value={displayRate}
                                                                        onChange={(e) => handleCellChange(cellKey, originalRate !== "-" ? originalRate : "", e.target.value)}
                                                                        sx={{ 
                                                                            width: 75, 
                                                                            '& .MuiInputBase-input': { 
                                                                                p: '4px 6px', 
                                                                                fontSize: '0.75rem', 
                                                                                textAlign: 'center', 
                                                                                fontWeight: isEdited ? 900 : 700,
                                                                                color: isEdited ? THEME.ACCENT_GOLD : THEME.TEXT_PRIMARY,
                                                                            },
                                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                                borderColor: isEdited ? THEME.ACCENT_GOLD : `${THEME.BORDER_DARK}66`
                                                                            },
                                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                                borderColor: THEME.ACCENT_GOLD
                                                                            },
                                                                            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                                borderColor: `${THEME.ACCENT_GOLD} !important`
                                                                            }
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                            );
                                                        })}
                                                    </TableRow>
                                                ))
                                            ))
                                        ))}
                                        {otaMeta.roomMappings.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={8} align="center" sx={{ py: 6, color: THEME.TEXT_PRIMARY, borderBottom: 'none' }}>
                                                    No Room Mappings Found
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Bulk Update Modal */}
            <Dialog 
                open={bulkOpen} 
                onClose={() => !isBulkSyncing && setBulkOpen(false)} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        border: `1px solid ${THEME.BORDER_DARK}`,
                        bgcolor: THEME.DEEP_DARK,
                        color: THEME.TEXT_PRIMARY,
                        boxShadow: `0 20px 25px -5px ${THEME.STRICT_DARK_BORDER}`
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: THEME.ACCENT_GOLD, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${THEME.BORDER_DARK}33`, pb: 1.5 }}>
                    Bulk Update Rates
                    <IconButton size="small" onClick={() => setBulkOpen(false)} disabled={isBulkSyncing} sx={{ color: THEME.TEXT_SECONDARY }}>
                        <CloseRounded fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ color: THEME.TEXT_SECONDARY, '&.Mui-focused': { color: THEME.ACCENT_GOLD } }}>Room Type</InputLabel>
                            <Select 
                                value={bulkRoomId} 
                                label="Room Type" 
                                onChange={(e) => setBulkRoomId(e.target.value)}
                                sx={{
                                    color: THEME.TEXT_PRIMARY,
                                    bgcolor: THEME.PANEL_DARK,
                                    '.MuiOutlinedInput-notchedOutline': { borderColor: THEME.BORDER_DARK },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD },
                                    '.MuiSvgIcon-root': { color: THEME.ACCENT_GOLD }
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            bgcolor: THEME.DEEP_DARK,
                                            color: THEME.TEXT_PRIMARY,
                                            border: `1px solid ${THEME.BORDER_DARK}`
                                        }
                                    }
                                }}
                            >
                                {otaMeta.roomMappings.map((room) => (
                                    <MenuItem key={room.RoomId} value={room.RoomId} sx={{ '&:hover': { bgcolor: THEME.PANEL_DARK } }}>
                                        {room.RoomName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small" disabled={!bulkRoomId}>
                            <InputLabel sx={{ color: THEME.TEXT_SECONDARY, '&.Mui-focused': { color: THEME.ACCENT_GOLD } }}>Rate Plan</InputLabel>
                            <Select 
                                value={bulkPlanId} 
                                label="Rate Plan" 
                                onChange={(e) => setBulkPlanId(e.target.value)}
                                sx={{
                                    color: THEME.TEXT_PRIMARY,
                                    bgcolor: THEME.PANEL_DARK,
                                    '.MuiOutlinedInput-notchedOutline': { borderColor: THEME.BORDER_DARK },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD },
                                    '.MuiSvgIcon-root': { color: THEME.ACCENT_GOLD }
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            bgcolor: THEME.DEEP_DARK,
                                            color: THEME.TEXT_PRIMARY,
                                            border: `1px solid ${THEME.BORDER_DARK}`
                                        }
                                    }
                                }}
                            >
                                {otaMeta.roomMappings.find(r => String(r.RoomId) === String(bulkRoomId))?.RoomRatePlans?.map((plan) => (
                                    <MenuItem key={plan.RatePlanId} value={plan.RatePlanId} sx={{ '&:hover': { bgcolor: THEME.PANEL_DARK } }}>
                                        {plan.RatePlanName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ color: THEME.TEXT_SECONDARY, '&.Mui-focused': { color: THEME.ACCENT_GOLD } }}>Target OTAs</InputLabel>
                            <Select 
                                multiple 
                                value={bulkOtas} 
                                onChange={(e) => setBulkOtas(e.target.value)} 
                                input={<OutlinedInput label="Target OTAs" />} 
                                renderValue={(s) => s.map(code => otaMeta.otas.find(o => o.OTACode === code)?.OTAName).join(', ')}
                                sx={{
                                    color: THEME.TEXT_PRIMARY,
                                    bgcolor: THEME.PANEL_DARK,
                                    '.MuiOutlinedInput-notchedOutline': { borderColor: THEME.BORDER_DARK },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD },
                                    '.MuiSvgIcon-root': { color: THEME.ACCENT_GOLD }
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            bgcolor: THEME.DEEP_DARK,
                                            color: THEME.TEXT_PRIMARY,
                                            border: `1px solid ${THEME.BORDER_DARK}`
                                        }
                                    }
                                }}
                            >
                                {otaMeta.otas.map((ota) => (
                                    <MenuItem key={ota.OTACode} value={ota.OTACode} sx={{ '&:hover': { bgcolor: THEME.PANEL_DARK } }}>
                                        <Checkbox 
                                            size="small" 
                                            checked={bulkOtas.indexOf(ota.OTACode) > -1} 
                                            sx={{ color: THEME.BORDER_DARK, '&.Mui-checked': { color: THEME.ACCENT_GOLD } }}
                                        />
                                        <ListItemText primary={ota.OTAName} sx={{ color: THEME.TEXT_PRIMARY }} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Stack direction="row" spacing={2}>
                            <TextField 
                                fullWidth 
                                size="small" 
                                type="date" 
                                label="Start Date" 
                                InputLabelProps={{ shrink: true, sx: { color: THEME.TEXT_SECONDARY, '&.Mui-focused': { color: THEME.ACCENT_GOLD } } }} 
                                value={bulkStartDate} 
                                onChange={e => setBulkStartDate(e.target.value)} 
                                InputProps={{
                                    sx: {
                                        borderRadius: 2,
                                        color: THEME.TEXT_PRIMARY,
                                        bgcolor: THEME.PANEL_DARK,
                                        '.MuiOutlinedInput-notchedOutline': { borderColor: THEME.BORDER_DARK },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD }
                                    }
                                }}
                            />
                            <TextField 
                                fullWidth 
                                size="small" 
                                type="date" 
                                label="End Date" 
                                InputLabelProps={{ shrink: true, sx: { color: THEME.TEXT_SECONDARY, '&.Mui-focused': { color: THEME.ACCENT_GOLD } } }} 
                                value={bulkEndDate} 
                                onChange={e => setBulkEndDate(e.target.value)} 
                                InputProps={{
                                    sx: {
                                        borderRadius: 2,
                                        color: THEME.TEXT_PRIMARY,
                                        bgcolor: THEME.PANEL_DARK,
                                        '.MuiOutlinedInput-notchedOutline': { borderColor: THEME.BORDER_DARK },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD }
                                    }
                                }}
                            />
                        </Stack>

                        <TextField 
                            fullWidth 
                            size="small" 
                            label="New Rate (₹)" 
                            type="number" 
                            value={bulkPrice} 
                            onChange={e => setBulkPrice(e.target.value)} 
                            InputLabelProps={{ sx: { color: THEME.TEXT_SECONDARY, '&.Mui-focused': { color: THEME.ACCENT_GOLD } } }}
                            InputProps={{
                                sx: {
                                    borderRadius: 2,
                                    color: THEME.TEXT_PRIMARY,
                                    bgcolor: THEME.PANEL_DARK,
                                    '.MuiOutlinedInput-notchedOutline': { borderColor: THEME.BORDER_DARK },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD }
                                }
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1, borderTop: `1px solid ${THEME.BORDER_DARK}33` }}>
                    <Button 
                        onClick={() => setBulkOpen(false)} 
                        disabled={isBulkSyncing}
                        sx={{ color: THEME.TEXT_SECONDARY, fontWeight: 700 }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        disableElevation
                        onClick={handleBulkSave} 
                        disabled={isBulkSyncing}
                        startIcon={isBulkSyncing && <CircularProgress size={16} sx={{ color: THEME.DEEP_DARK }} />}
                        sx={{ 
                            bgcolor: THEME.ACCENT_GOLD, 
                            color: THEME.DEEP_DARK, 
                            fontWeight: 800, 
                            borderRadius: 2, 
                            px: 3, 
                            textTransform: 'none',
                            '&:hover': { bgcolor: THEME.BORDER_DARK, color: '#FFFFFF' } 
                        }}
                    >
                        {isBulkSyncing ? "Syncing..." : "Update & Sync"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default RateUpdates;